<template>
  <div>
    <msp-edr-affected-devices-table
      :mitre-data="mitreData"
      :edr-affected-devices="edrAffectedDevices"
      :edr-affected-devices-loading="edrAffectedDevicesLoading"
      :edr-affected-devices-total="edrAffectedDevicesTotal"
      :show-expander="showExpander"
      :findings-to-load="findingsToLoad"
      :event-trigger="ticket.eventTrigger"
      @load-more="loadMoreItems"
    />
    <findings-full-details
      v-if="isBlockedConnection"
      :ticket="ticket"
      :show-title="false"
      class="mt-4"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, toRefs } from "vue";
import { storeToRefs } from "pinia";
import { useMspTicketsStore } from "@/_store/msp/tickets/msp-tickets.module.ts";
import type { MspTicketDetails } from "@/_store/msp/tickets/msp-tickets.module.ts";
import { TicketType } from "@/constants/tickets.ts";
import EdrAffectedDevicesTable from "../../tickets/ticket-preview-details/base-components/EdrAffectedDevicesTable.vue";
import FindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/FindingsFullDetails.vue";

export default defineComponent({
  components: {
    MspEdrAffectedDevicesTable: EdrAffectedDevicesTable,
    FindingsFullDetails,
  },
  props: {
    ticket: {
      type: Object as PropType<MspTicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const ticketStore = useMspTicketsStore();
    const { fullDetails } = storeToRefs(ticketStore);
    const { edrAffectedDevices, edrAffectedDevicesLoading, edrAffectedDevicesTotal } = toRefs(
      fullDetails.value
    );
    const page = ref(0);

    const mitreData = computed(() => {
      return props.ticket.sections?.edrMetaData?.mitre ?? [];
    });

    const showExpander = computed(() => {
      return props.ticket.sections?.edrMetaData?.showExpandedFindings;
    });

    const findingsToLoad = computed(() => {
      return edrAffectedDevicesTotal.value - (edrAffectedDevices.value ?? []).length;
    });

    const isBlockedConnection = computed(
      () => props.ticket.eventType === TicketType.BLOCKED_CONNECTION
    );

    const getItems = async (virtualScroll = false) => {
      await ticketStore.getEdrAffectedDevices({
        eventId: props.ticket.eventId,
        page: page.value,
        virtualScroll,
      });
    };

    const loadMoreItems = () => {
      page.value += 1;
      getItems(true);
    };

    onMounted(async () => {
      await getItems();
    });

    return {
      page,
      edrAffectedDevices,
      edrAffectedDevicesLoading,
      edrAffectedDevicesTotal,
      mitreData,
      showExpander,
      findingsToLoad,
      isBlockedConnection,
      loadMoreItems,
    };
  },
});
</script>

<style lang="scss" scoped>
.expand-all-icon {
  .v-icon::before {
    color: rgb(var(--v-theme-white)) !important;
  }
}

.load-more-section {
  border-left: 1px solid rgb(var(--v-theme-indigo-pale));
  border-right: 1px solid rgb(var(--v-theme-indigo-pale));
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(*) {
  .v-data-table__expanded__content {
    box-shadow: unset !important;
  }

  .v-btn--fab.v-size--small {
    height: 30px;
    width: 30px;
  }
  .v-btn {
    padding: 0 !important;
  }

  .v-table__wrapper {
    box-shadow: unset !important;
    border-radius: unset !important;
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  .full-details-wrapper {
    border: none !important;
  }

  .findings-wrapper {
    padding: 0 !important;
  }
}
</style>
